import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faFilePdf, faLanguage, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Table from '@shared/components/Table.jsx';
import DeleteModal from '@shared/components/DeleteModal.jsx';
import SchoolForm from './SchoolForm';
import { useSchools } from '../hooks/useSchools.js';
import SchoolTranslations from './SchoolTranslations.jsx';
import { useStatusBadge } from '@shared/components/StatusBadge.jsx';
import { useReportGenerator } from '@shared/hooks/useReportGenerator.js';

export default function Schools() {
    const { t } = useTranslation();
    const {
        data,
        selectedData,
        setSelectedData,
        schoolData,
        modalState,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchType,
        searchOptions,
        handleOpenModal,
        handleCloseModal,
        handleAddCloseModal,
        handleEditSchool,
        handleConfirmDelete,
        handleSearchClick,
        setSearchQuery,
        setSearchType,
        handleSort,
        setPageSize,
        setpageNumber,
        selectedSchoolName,
        fetchData,
        schoolService
    } = useSchools();

    const { statusBadge } = useStatusBadge();

    // Use the global report generator hook
    const { generatePdfReport, pdfLoading } = useReportGenerator({
        title: t('nav.schools.title'),
        columns: [
            { key: 'shortName', label: t('nav.schools.table.headers.shortName') },
            { key: 'name', label: t('nav.schools.table.headers.name') },
            { 
                key: 'country', 
                label: t('nav.schools.table.headers.country'),
                render: (item) => {
                    const countryMap = {
                        'BG': 'България',
                        'US': 'United States',
                        'DE': 'Germany',
                        'FR': 'France',
                        'IT': 'Italy',
                        'ES': 'Spain'
                    };
                    return countryMap[item.country] || item.country;
                }
            },
            { key: 'postcode', label: t('nav.schools.table.headers.postcode') },
            { key: 'address1', label: t('nav.schools.table.headers.city') },
            { key: 'address2', label: t('nav.schools.table.headers.address') },
        ],
        fileName: 'schools_report',
        companyName: '',
        reportService: schoolService.getSchoolsReportData,
        customStyles: {
        
        }
    });

    useEffect(() => {
        fetchData(schoolService.getSchools, {
            searchQuery,
            sortBy: sortBy || 'name',
            sortDir: sortDir
        });
    }, [pageNumber, pageSize, sortBy, sortDir]);

    const columns = [
        { key: 'address1', label: t('nav.schools.table.headers.city'), sortable: true },
        { key: 'name', label: t('nav.schools.table.headers.name'), sortable: true },
        { key: 'active', label: t('nav.schools.table.headers.active'), sortable: true },
        { key: 'hasTranslations', label: t('nav.schools.table.headers.translations'), sortable: true }
    ];

    const actionButtons = (
        <>
            <button
                className="icon-button"
                onClick={() => handleOpenModal('add')}
                title={t('common.add')}
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={handleEditSchool}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>

            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('translations')}
                title={t('common.translations')}
            >
                <FontAwesomeIcon icon={faLanguage} />
            </button>

            <button
                className={`icon-button delete ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('delete')}
                title={t('common.remove')}
            >
                <FontAwesomeIcon icon={faTrash} />
            </button>

                   <button
                       className={`icon-button ${pdfLoading ? 'disabled-link' : ''}`}
                       onClick={generatePdfReport}
                       disabled={pdfLoading}
                       title={pdfLoading ? "Генериране на PDF..." : "Генериране на PDF отчет"}
                   >
                       <FontAwesomeIcon icon={pdfLoading ? faSpinner : faFilePdf} spin={pdfLoading} />
                   </button>
        </>
    );

    const handleCheckboxChange = (index) => {
        const school = data[index];
        setSelectedData(selectedData === school.id ? null : school.id);
    };

    return (
        <>
            <Table
                // Data and state
                data={data}
                loading={false}
                error={null}
                selectedData={selectedData}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchType={searchType}
                searchOptions={searchOptions}

                // Column configuration
                columns={columns}

                // Title and actions
                title={t('nav.schools.title')}
                actionButtons={actionButtons}

                // Handlers
                handleSort={handleSort}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchClick={handleSearchClick}
                setSearchQuery={setSearchQuery}
                setSearchType={setSearchType}
                setPageSize={setPageSize}
                setpageNumber={setpageNumber}
                getData={schoolService.getSchools}

                // Custom renderers
                renderCell={(item, column) => statusBadge(item, column)}
              
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    selectedData={selectedData}
                    name={selectedSchoolName}
                    entityType="schools"
                />
            )}
            {modalState.visible && (modalState.type === 'edit' || modalState.type === 'add') && (
                <SchoolForm
                    onClose={handleAddCloseModal}
                    school={schoolData}
                    selectedData={selectedData}
                    fetchData={fetchData}
                />
            )}
            {modalState.visible && modalState.type === 'translations' && (
                <SchoolTranslations
                    onClose={handleCloseModal}
                    school={data.find(school => school.id === selectedData)}
                />
            )}
        </>
    );
}
